import { useQuery } from '@tanstack/react-query';
import type { FundingStatistics, Money } from '@/lib/api-admin/types';
import { apiClient } from '@/lib/apiClient';

export const fundingStatisticsQueryKey = (campaignId: string) =>
  ['admin', 'funding-statistics', campaignId] as const;

/**
 * QA-398 — BE(settlement FundingStatisticsDto)와 화면(스펙 FundingStatistics)의 필드 이름이 달라 결제 완료·총 모금액·
 * 평균 후원금이 비어 보였다. BE 이름을 화면 이름으로 맞춘다(스펙 이름으로 오면 그대로):
 *   paidPledgeCount → totalPaidCount · cancelledPledgeCount → totalCancelledCount · refundedPledgeCount → totalRefundedCount
 *   currentAmount → totalAmount · rewards[].currentQuantity → rewardBreakdown[].selectedQuantity
 * 평균 후원금은 BE avgPledgeAmount(settlement #9, 모금액 ÷ 결제 완료 수). 결제 완료 = PAID + SETTLED.
 */
export const normalizeFundingStatistics = (raw: unknown): FundingStatistics => {
  const d = (raw ?? {}) as Record<string, unknown>;
  const num = (...keys: string[]): number | undefined => {
    for (const k of keys) if (typeof d[k] === 'number') return d[k] as number;
    return undefined;
  };
  const totalAmount = (d.totalAmount ?? d.currentAmount) as Money | undefined;
  const paid = num('totalPaidCount', 'paidPledgeCount') ?? 0;
  const avg = d.avgPledgeAmount as Money | undefined;
  const rawRewards = (Array.isArray(d.rewardBreakdown) ? d.rewardBreakdown : Array.isArray(d.rewards) ? d.rewards : []) as Record<
    string,
    unknown
  >[];
  return {
    ...(d as unknown as FundingStatistics),
    totalPledgeCount: num('totalPledgeCount') ?? 0,
    totalPaidCount: paid,
    totalCancelledCount: num('totalCancelledCount', 'cancelledPledgeCount') ?? 0,
    totalRefundedCount: num('totalRefundedCount', 'refundedPledgeCount') ?? 0,
    totalAmount: totalAmount as Money,
    achievementRate: num('achievementRate') ?? 0,
    ...(avg ? { avgPledgeAmount: avg } : {}),
    rewardBreakdown: rawRewards.map((r) => ({
      rewardId: String(r.rewardId ?? ''),
      tierName: String(r.tierName ?? ''),
      selectedQuantity: typeof r.selectedQuantity === 'number' ? r.selectedQuantity : Number(r.currentQuantity ?? 0),
      remainingQuantity: (r.remainingQuantity as number | null | undefined) ?? null,
    })) as FundingStatistics['rewardBreakdown'],
  };
};

export const useFundingStatisticsQuery = (campaignId: string | undefined) =>
  useQuery<FundingStatistics>({
    queryKey: fundingStatisticsQueryKey(campaignId ?? ''),
    enabled: Boolean(campaignId),
    queryFn: async () => {
      const [data] = await apiClient.request(
        'get',
        '/admin/v1/funding-campaigns/{campaignId}/statistics',
        { params: { path: { campaignId: campaignId as string } } },
      );
      return normalizeFundingStatistics(data);
    },
  });
